import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { ThreadListItem, ThreadDetail, LabelDef, ViewDef } from "../../shared/types";

function errorMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong loading this.";
}

export function useViews(mailboxId: string | null) {
  const [views, setViews] = useState<ViewDef[]>([]);
  const reload = useCallback(async () => {
    if (!mailboxId) return setViews([]);
    const res = await api.get<{ views: ViewDef[] }>(`/api/mailboxes/${mailboxId}/views`);
    setViews(res.views);
  }, [mailboxId]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { views, reload };
}

export function useLabels(mailboxId: string | null) {
  const [labels, setLabels] = useState<LabelDef[]>([]);
  const reload = useCallback(async () => {
    if (!mailboxId) return setLabels([]);
    const res = await api.get<{ labels: LabelDef[] }>(`/api/mailboxes/${mailboxId}/labels`);
    setLabels(res.labels);
  }, [mailboxId]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { labels, reload };
}

const MAX_AUTO_RETRIES = 8;

// Pure fast read against already-indexed mail — never triggers a Gmail sync
// itself, so switching views is always instant regardless of import state.
// Syncing is driven independently by useMailboxSync below.
export function useThreads(mailboxId: string | null, view: string, query: string) {
  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const consecutiveErrorsRef = useRef(0);

  const reload = useCallback(async () => {
    if (!mailboxId) return setThreads([]);
    setLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (query.trim()) params.set("q", query.trim());
      const res = await api.get<{ threads: ThreadListItem[] }>(`/api/mailboxes/${mailboxId}/threads?${params}`);
      setThreads(res.threads);
      setError(null);
      consecutiveErrorsRef.current = 0;
    } catch (e) {
      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current <= MAX_AUTO_RETRIES) {
        setRetryTick((t) => t + 1);
      } else {
        setError(errorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  }, [mailboxId, view, query]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (retryTick === 0) return;
    const timer = setTimeout(reload, 3000);
    return () => clearTimeout(timer);
  }, [retryTick, reload]);

  return { threads, loading, error, reload };
}

/** Drives the background Gmail sync independently of whatever view is
 * showing. Calls onSynced after every successful tick so callers can
 * refresh whatever list is currently visible. */
export function useMailboxSync(mailboxId: string | null, onSynced: () => void) {
  const [stillSyncing, setStillSyncing] = useState(false);
  const [importProgress, setImportProgress] = useState<{ indexedCount: number; totalEstimate: number | null } | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const consecutiveErrorsRef = useRef(0);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const syncNow = useCallback(
    async (force = false) => {
      if (!mailboxId) return;
      try {
        const res = await api.post<{ stillSyncing: boolean; indexedCount: number; totalEstimate: number | null }>(
          `/api/mailboxes/${mailboxId}/sync`,
          { force },
        );
        setStillSyncing(res.stillSyncing);
        setImportProgress(res.stillSyncing ? { indexedCount: res.indexedCount, totalEstimate: res.totalEstimate } : null);
        consecutiveErrorsRef.current = 0;
        onSyncedRef.current();
      } catch {
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current <= MAX_AUTO_RETRIES) setRetryTick((t) => t + 1);
      }
    },
    [mailboxId],
  );

  useEffect(() => {
    syncNow();
  }, [syncNow]);

  useEffect(() => {
    if (retryTick === 0) return;
    const timer = setTimeout(() => syncNow(), 3000);
    return () => clearTimeout(timer);
  }, [retryTick, syncNow]);

  // Keep nudging a large first-time import forward automatically instead of
  // requiring the tab to be manually reloaded.
  useEffect(() => {
    if (!stillSyncing) return;
    const timer = setTimeout(() => syncNow(), 2000);
    return () => clearTimeout(timer);
  }, [stillSyncing, syncNow]);

  return { stillSyncing, importProgress, syncNow };
}

export function useThreadDetail(mailboxId: string | null, threadId: string | null) {
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!mailboxId || !threadId) return setThread(null);
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ThreadDetail>(`/api/mailboxes/${mailboxId}/threads/${threadId}`);
      setThread(res);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [mailboxId, threadId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { thread, loading, error, reload };
}
